import { Injectable, inject, isDevMode } from '@angular/core';
import { TelemetryService } from '@core/error-handling/telemetry.service';

/**
 * Logger applicatif admin.
 *
 * - Centralise les sorties pour eviter les `console.*` epars.
 * - En production, les erreurs sont forwardees a PostHog (error tracking)
 *   via le `TelemetryService` sans toucher au code appelant (cf. `error()`).
 * - En dev (`isDevMode()`), tous les niveaux sont affiches dans la console
 *   pour ne pas perdre la DX.
 *
 * Usage :
 * ```ts
 * private readonly logger = inject(LoggerService);
 * this.logger.error('CheckoutFailed', err, { orderId });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class LoggerService {
  private readonly telemetry = inject(TelemetryService);

  /** Erreur grave : doit remonter sur le service de monitoring en prod. */
  error(scope: string, error: unknown, context?: Record<string, unknown>): void {
    if (isDevMode()) {
      console.error(`[${scope}]`, error, context ?? '');
    }
    if (error instanceof Error) {
      this.telemetry.captureException(error, { scope, ...context });
    } else {
      this.telemetry.captureMessage(`[${scope}] ${String(error)}`, 'error');
    }
  }

  /** Avertissement : situation suspecte mais non bloquante. */
  warn(scope: string, message: string, context?: Record<string, unknown>): void {
    if (isDevMode()) {
      console.warn(`[${scope}]`, message, context ?? '');
    }
    this.telemetry.addBreadcrumb(scope, message, 'warning');
  }

  /** Info debug : visible uniquement en dev. */
  info(scope: string, message: string, context?: Record<string, unknown>): void {
    if (isDevMode()) {
      console.info(`[${scope}]`, message, context ?? '');
    }
  }
}
